import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import { ROUTE_DATA_SOURCES, ROUTE_PIPELINES } from 'js/constants/pathnames';
import {
  FLOW_TYPE_API_SERVER,
  FLOW_TYPE_CUSTOM,
  PIPELINE_ACTION_ACTIVATE,
  PIPELINE_ACTION_DELETE,
  PIPELINE_ACTION_PAUSE,
  DELETE_DISABLED_MESSAGES,
} from 'js/constants/pipelineConstants';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { restrictMenuForRole } from 'js/lib/ACLUtils';
import { isFullScreenCanvasURL } from 'js/lib/URLUtils';
import { ReactComponent as ActivateIcon } from 'new/components/icons/bolt.svg';
import { ReactComponent as DuplicateIcon } from 'new/components/icons/copyAlt.svg';
import { ReactComponent as EditFileIcon } from 'new/components/icons/editFile.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-info.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as PauseIcon } from 'new/components/icons/pause.svg';
import { ReactComponent as PlayIcon } from 'new/components/icons/play.svg';
import { editModalsEnabled } from 'new/constants/featureFlags';
import React from 'react';
import RoutePrefixContext from '../../../../new/contexts/RoutePrefixContext';
import Menu from '../../../components/elements/menu/Menu';
import Constants from '../../../Constants';
import UIUtils from '../../../lib/UIUtils';
import { createActionValidator, createDeleteActionValidator } from 'js/lib/flowValidationUtils';

class DataSourceDetailActions extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this._handlePauseClicked = this._handlePauseClicked.bind(this);
    this._handleActivateClicked = this._handleActivateClicked.bind(this);
    this._handleRunNowClicked = this._handleRunNowClicked.bind(this);
    this._handleDeleteClicked = this._handleDeleteClicked.bind(this);
    this._handleCopyClicked = this._handleCopyClicked.bind(this);
    this._handlePausePipelineClicked = this._handlePausePipelineClicked.bind(this);
    this._handleActivatePipelineClicked = this._handleActivatePipelineClicked.bind(this);
    this._handleDeletePipelineClicked = this._handleDeletePipelineClicked.bind(this);
    this._handleOpenDetails = this._handleOpenDetails.bind(this);
  }

  render() {
    const {
      data,
      flow,
      readOnly,
      inPipeline,
      isPausing,
      isActivating,
      isCopying,
      isDeleting,
      ingestionToggleMenuItems = [],
      selectActions,
      connectionCategory,
      connectionOrVendorType,
      isSqlAutomation,
      hideMenuDetails,
      hasDestination,
      location: { pathname },
    } = this.props;

    const status = data.status;
    const flowType = data?.flow_type || flow?.flow_type;
    const isRAGFlowType = flowType === 'rag';
    const isApiServerFlowType = flowType === FLOW_TYPE_API_SERVER;
    // BYO flows are a one-shot batch job, not a streaming pipeline, and have no
    // nexset node. They get a tailored action set: no Activate/Pause lifecycle,
    // a "Set up destination" entry (because there is no nexset to add a sink
    // from), and a "Run now" entry once a destination exists.
    const isCustomFlowType = flowType === FLOW_TYPE_CUSTOM;
    const isSourceAutoGenerated = UIUtils.isSourceAutoGenerated(data);
    const isFullScreenCanvas = isFullScreenCanvasURL(pathname);
    const disableCopy = isFullScreenCanvas || UIUtils.disallowSourceCopy(connectionCategory, data);
    const allowDelete =
      (!isFullScreenCanvas || isSourceAutoGenerated) &&
      (!selectActions || selectActions.includes('delete'));
    const allowDeleteFlow =
      !isFullScreenCanvas && (!selectActions || selectActions.includes('deleteFlow'));
    const baseShowRunNow = shouldRenderRunAction(data, connectionCategory);
    const isCapsuleSource = isCapsuleCluster(data);
    const isCapsuleSourceInPipeline = !!inPipeline && isCapsuleSource;
    const hideForCapsuleNoSink = isCapsuleSourceInPipeline && !hasDestination;
    const showRunNow = baseShowRunNow && !hideForCapsuleNoSink;

    let menuItems = [
      {
        label: 'Source',
        type: 'heading',
      },
    ];

    if (!hideMenuDetails) {
      menuItems.push({
        label: 'Details',
        icon: <DetailsIcon />,
        onClick: this._handleOpenDetails,
      });
    }

    const actionsValidationForFlow = createActionValidator(flow ?? data, isPausing, isActivating);

    if (isCustomFlowType) {
      // BYO is a one-shot batch flow with no streaming Activate/Pause lifecycle,
      // so the status switch is skipped entirely for the source node. The
      // destination ("Set up destination") and "Run now" actions live on the
      // synthetic Code node (CodeNode) — the BYO stand-in for the nexset — to
      // mirror how normal flows add a destination from the nexset, not the
      // source. The source node keeps only Details / Edit / Duplicate / Delete.
    } else {
      switch (status) {
        case Constants.SourceStatus.ACTIVE:
          if (!selectActions || selectActions.includes('activate')) {
            if (!isCapsuleSource) {
              menuItems.push({
                label: isPausing ? 'Pausing...' : 'Pause',
                icon: <PauseIcon />,
                onClick: this._handlePauseClicked,
                ...actionsValidationForFlow,
              });
            }

            if (showRunNow) {
              menuItems.push({
                label: isPausing ? 'Pausing...' : isActivating ? 'Activating...' : 'Run Now',
                icon: <PlayIcon />,
                onClick: this._handleRunNowClicked,
                ...actionsValidationForFlow,
              });
            }
          }

          break;

        case Constants.SourceStatus.PAUSED:
          if (!isCapsuleSource && (!selectActions || selectActions.includes('activate'))) {
            menuItems.push({
              label: isActivating ? 'Activating...' : 'Activate',
              icon: <ActivateIcon />,
              onClick: this._handleActivateClicked,
              ...actionsValidationForFlow,
            });
          }

          break;

        case Constants.SourceStatus.RATE_LIMITED:
          break;

        default:
          if (!isCapsuleSource && (!selectActions || selectActions.includes('activate'))) {
            menuItems.push({
              label: isActivating ? 'Activating...' : 'Activate',
              icon: <ActivateIcon />,
              onClick: this._handleActivateClicked,
              ...actionsValidationForFlow,
            });
          }
      }
    }

    const editHref = {
      replication: `${this.context}${ROUTE_PIPELINES}/replication/source/${data.id}/edit`,
      spark: `${this.context}${ROUTE_PIPELINES}/spark/source/${data.id}/edit`,
      cdc: `${this.context}${ROUTE_PIPELINES}/cdc/source/${data.id}/edit`,
      in_memory: `${this.context}${ROUTE_PIPELINES}/in_memory/source/${data.id}/edit`,
      elt: `${this.context}${ROUTE_PIPELINES}/elt/source/${data.id}/edit`,
      custom: `${this.context}${ROUTE_PIPELINES}/custom/source/${data.id}/edit`,
      streaming: `${this.context}${ROUTE_DATA_SOURCES}/${data.id}/edit`,
    };

    const isModalEnabled = editModalsEnabled()?.source?.[flowType];
    const isSqlRunner = isSqlAutomation === true;
    const useModalForEdit = isModalEnabled || isSqlRunner;

    let editMenuItem = {
      label: 'Edit',
      icon: <EditFileIcon />,
      href: !useModalForEdit ? editHref[flowType] || editHref.streaming : undefined,
      onClick: useModalForEdit ? this._handleEditClicked(flowType) : undefined,
    };

    if ((!selectActions || selectActions.includes('edit')) && !isApiServerFlowType) {
      menuItems.push(editMenuItem);
    }

    if (!isSourceAutoGenerated && !disableCopy) {
      if ((!selectActions || selectActions.includes('duplicate')) && !isApiServerFlowType) {
        menuItems.push({
          label: isCopying ? 'Duplicating...' : 'Duplicate',
          icon: <DuplicateIcon />,
          disable: isCopying,
          onClick: this._handleCopyClicked,
        });
      }
    }

    if (allowDelete) {
      const deleteValidation = createDeleteActionValidator(
        status,
        isDeleting,
        isPausing,
        DELETE_DISABLED_MESSAGES.SOURCE,
      );

      menuItems.push({
        type: 'dialog',
        label: isDeleting ? 'Deleting...' : 'Delete',
        icon: <DeleteIcon />,
        color: 'error',
        dialogMessage: `Please confirm you want to delete source ${data.name}(ID: ${data.id})`,
        primaryButtonColor: 'error',
        discardLabel: 'Cancel',
        confirmLabel: 'Delete',
        onClick: this._handleDeleteClicked,
        ...deleteValidation,
      });
    }

    menuItems.push(...ingestionToggleMenuItems);

    // BYO flows have no streaming lifecycle, so they skip the
    // Activate Flow / Pause Flow controls. The Flow heading is only added when
    // there is at least one flow-level action to show under it.
    const flowSectionItems = [];

    if (!isCustomFlowType) {
      flowSectionItems.push(
        {
          label: 'Activate Flow',
          icon: <ActivateIcon />,
          onClick: this._handleActivatePipelineClicked,
          ...actionsValidationForFlow,
        },
        {
          label: 'Pause Flow',
          icon: <PauseIcon />,
          onClick: this._handlePausePipelineClicked,
          ...actionsValidationForFlow,
        },
      );
    }

    if (allowDeleteFlow) {
      flowSectionItems.push({
        label: 'Delete Flow',
        icon: <DeleteIcon />,
        color: 'error',
        onClick: this._handleDeletePipelineClicked,
      });
    }

    if (flowSectionItems.length > 0) {
      menuItems.push(
        {
          label: 'Flow',
          type: 'heading',
        },
        ...flowSectionItems,
      );
    }

    menuItems = restrictMenuForRole(menuItems, data);

    if (this.props.additionalMenuItems) {
      this.props.additionalMenuItems.forEach((item) => {
        if (item.position === 'bottom') {
          menuItems.push(item);
        } else {
          menuItems.unshift(item);
        }
      });
    }

    return (
      <div className="detail-actions detail-actions-gap">
        <div data-testid={`menu-actions-container-${data.id}`} aria-label="menu-actions-container">
          {!isRAGFlowType && !readOnly && !isSourceAutoGenerated ? (
            <Menu
              menuButtonClassName={this.props.menuButtonClassName}
              horizontalDots={this.props.horizontalDots}
              items={menuItems}
              inPipeline={inPipeline}
            />
          ) : null}
        </div>
      </div>
    );
  }

  _handlePauseClicked(event) {
    this.props.pauseSource(this.props.data.id);
  }

  _handleActivateClicked(event) {
    this.props.activateSource(this.props.data.id);
  }

  _handleEditClicked = (flowType) => (event) => {
    const { data, setAction, isSqlAutomation: isSqlRunner } = this.props;

    setAction?.({
      id: data.id,
      type: 'edit',
      flowType: flowType,
      resource: RESOURCE_TYPE_SOURCE,
      ...(isSqlRunner ? { queryParams: { selectedConnection: CONNECTOR_CODE_SQL_RUNNER } } : {}),
    });
  };

  _handleRunNowClicked(event) {
    // BYO "Run now" reuses the existing source run action, which the canvas
    // already uses for scheduled sources (PUT /data_sources/{id}/run_now). A
    // dedicated backend-listing `schedule_now` job-run path was scaffolded on
    // this branch but removed as dead/unwired code (never called from either
    // BYO shell); it needed a session token + generated runId that aren't
    // available here, so we intentionally reuse runSource for now.
    // TODO(byo): NEX-22637 — revisit once the Job Workbench run UI is
    // (re)built and a runId/token source is wired into this action path.
    this.props.runSource(this.props.data.id);
  }

  _handleDeleteClicked(event) {
    this.props.deleteSource(this.props.data.id);
  }

  _handleCopyClicked(event) {
    this.props.copySource(this.props.data.id);
  }

  _handlePausePipelineClicked() {
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_PAUSE);
  }

  _handleActivatePipelineClicked() {
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_ACTIVATE);
  }

  _handleDeletePipelineClicked() {
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_DELETE);
  }

  _handleOpenDetails(e) {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    const { data, onOpenDetailsMenu, openDetailDrawerInPortal, openDetailsModal } = this.props;
    if (typeof onOpenDetailsMenu === 'function') {
      onOpenDetailsMenu(data);
      return;
    }
    if (typeof openDetailsModal === 'function') {
      openDetailsModal(data);
      return;
    }
    if (typeof openDetailDrawerInPortal === 'function') {
      openDetailDrawerInPortal(data, {});
    }
  }
}

DataSourceDetailActions.defaultProps = {
  showStatus: true,
  readOnly: false,
  isManaged: false,
  hideMenuDetails: false,
};

export default DataSourceDetailActions;
